import { useState, type MouseEvent, type ReactNode } from "react";
import { Avatar } from "@design-system/Avatar/Avatar";
import { Button } from "@design-system/Button/Button";
import { SideNavHeader } from "@design-system/SideNav/SideNavHeader";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavPickerTrigger } from "@design-system/SideNav/SideNavPickerTrigger";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import type {
    SideNavForcedState,
    SideNavTarget,
} from "@design-system/SideNav/SideNav.types";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { PlusIcon, SupportIcon, WorkIcon } from "@icons";
import { taskCountTrailing } from "@components/navigation/sideNav/ActionsRow";

/** What every composition plate takes from the sandbox. */
export interface SideNavCompositionProps {
    className?: string;
    /** Anatomy state, painted on the first section's header and first row. */
    forcedRowState?: SideNavForcedState;
}

const withMember = (
    members: ReadonlySet<string>,
    member: string,
    isMember: boolean,
) => {
    const next = new Set(members);
    if (isMember) {
        next.add(member);
    } else {
        next.delete(member);
    }
    return next;
};

/**
 * Local state for a playground nav: links mark themselves active instead of
 * navigating away from the playground, sections and expandable rows toggle.
 */
export const usePlaygroundNav = ({
    activeId,
    closedSections = [],
    expandedRows = [],
}: {
    activeId: string;
    closedSections?: readonly string[];
    expandedRows?: readonly string[];
}) => {
    const [currentId, setCurrentId] = useState(activeId);
    const [closed, setClosed] = useState<ReadonlySet<string>>(
        () => new Set(closedSections),
    );
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(
        () => new Set(expandedRows),
    );

    return {
        link: (id: string) => ({
            to: `#${id}`,
            isActive: currentId === id,
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                setCurrentId(id);
            },
        }),
        section: (label: string) => ({
            label,
            isOpen: !closed.has(label),
            onOpenChange: (isOpen: boolean) =>
                setClosed((current) => withMember(current, label, !isOpen)),
        }),
        expansion: (id: string) => ({
            isExpanded: expanded.has(id),
            onExpandedChange: (isExpanded: boolean) =>
                setExpanded((current) => withMember(current, id, isExpanded)),
        }),
    };
};

export type PlaygroundNav = ReturnType<typeof usePlaygroundNav>;

export const PlaygroundHeader = ({
    name,
    shape,
}: {
    name: string;
    shape: "circle" | "square";
}) => (
    <SideNavHeader
        picker={
            <SideNavPickerTrigger
                avatar={<Avatar name={name} size="sm" shape={shape} />}
                label={name}
            />
        }
    />
);

/** New ⌘K and Actions, the top rows at every level but personal; `home` sits between them. */
export const PlaygroundTopRows = ({
    nav,
    taskCount,
    home,
}: {
    nav: PlaygroundNav;
    taskCount: number;
    home?: ReactNode;
}) => (
    <>
        <SideNavItem
            onClick={() => {}}
            icon={<PlusIcon />}
            label="New"
            trailing={<Shortcut keys="mod+k" size="sm" />}
            trailingVisibility="reveal"
        />
        {home}
        <SideNavItem
            {...nav.link("actions")}
            icon={<WorkIcon />}
            label="Actions"
            trailing={taskCountTrailing(taskCount)}
            trailingVisibility="reveal"
        />
    </>
);

/** The Clients section's `+`, also used as a row's hover action. */
export const PlaygroundAddClientButton = () => (
    <Button variant="secondary" size="xs" iconOnly aria-label="Add client">
        <PlusIcon />
    </Button>
);

/** A client's task rows, one per status; `target` maps a task id to where its row goes. */
export const PlaygroundTaskSubItems = ({
    target,
}: {
    target: (taskId: string) => SideNavTarget & { isActive?: boolean };
}) => (
    <>
        <SideNavSubItem
            {...target("close")}
            label="September close"
            status="unread"
            meta="1h"
        />
        <SideNavSubItem
            {...target("payroll")}
            label="Payroll review"
            status="inProgress"
            meta="3d"
        />
        <SideNavSubItem {...target("1099")} label="1099 filing" status="none" />
    </>
);

export const PlaygroundHelpButton = () => (
    <Button variant="primary" size="md" iconOnly aria-label="Help">
        <SupportIcon />
    </Button>
);

export const PlaygroundEntityIcon = ({ name }: { name: string }) => (
    <Avatar name={name} size="xs" shape="square" aria-hidden />
);
